import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Edit3, Save, Loader2, Languages, X, LogOut, Eye, Database, Upload } from "lucide-react";
import AdminTabs from "@/components/AdminTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "artlenss-admin-token";

const CATEGORY_OPTIONS = [
  "Commercial", "Documentary", "Drama", "Music Video",
  "Motion Graphics", "Product Photography", "Sound Recording", "Event",
  "Behind the Scenes",
];

const emptyForm = {
  id: null,
  slug: "",
  title_ar: "",
  title_en: "",
  short_ar: "",
  short_en: "",
  body_ar: "",
  body_en: "",
  client_ar: "",
  client_en: "",
  goal_ar: "",
  goal_en: "",
  timeline_ar: "",
  timeline_en: "",
  approach_ar: "",
  approach_en: "",
  approach_headline_ar: "",
  approach_headline_en: "",
  locations_ar: "",
  locations_en: "",
  team_ar: "",
  team_en: "",
  result_ar: "",
  result_en: "",
  image: "",
  video: "",
  external_url: "",
  year: new Date().getFullYear().toString(),
  category: "Commercial",
  equipment: [],
  tools: [],
  hide_meta: false,
  hide_approach: false,
  hide_highlights: false,
  hide_result: false,
  full_width_video: false,
  hero_layout: "",
  result_background: "",
  vision_title_ar: "",
  vision_title_en: "",
  vision_paragraphs_ar: "",
  vision_paragraphs_en: "",
  published: true,
  order: 0,
};

const AdminPortfolio = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showEditor, setShowEditor] = useState(false);
  const [showVision, setShowVision] = useState(false);
  const [translating, setTranslating] = useState({});
  const [uploading, setUploading] = useState({});

  const authHeaders = { Authorization: `Bearer ${token}` };

  const uploadImage = async (field, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت");
      return;
    }
    setUploading((s) => ({ ...s, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/uploads/image`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success("تم رفع الصورة ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل رفع الصورة");
    } finally {
      setUploading((s) => ({ ...s, [field]: false }));
    }
  };

  const loadProjects = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/portfolio/projects`, { headers: authHeaders });
      setProjects(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("انتهت الجلسة، أعد تسجيل الدخول");
      }
    }
  };

  useEffect(() => { loadProjects(); /* eslint-disable-next-line */ }, [token]);

  const doLogin = async () => {
    if (!password.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("مرحباً بك في لوحة المحفظة 🎬");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "كلمة المرور غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setProjects([]);
    toast("تم تسجيل الخروج");
  };

  const openEditor = (project = null) => {
    if (project) {
      setForm({ ...emptyForm, ...project });
      setShowVision(project.hero_layout === "vision" || !!project.vision_title_ar || !!project.vision_title_en);
    } else {
      setForm(emptyForm);
      setShowVision(false);
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setForm(emptyForm);
    setShowVision(false);
  };

  const translate = async (field) => {
    const arField = `${field}_ar`;
    const enField = `${field}_en`;
    if (!form[arField]?.trim()) {
      toast.error("اكتب النص العربي أولاً");
      return;
    }
    setTranslating((s) => ({ ...s, [field]: true }));
    try {
      const { data } = await axios.post(
        `${API}/admin/portfolio/translate`,
        { text: form[arField], target: "en" },
        { headers: authHeaders }
      );
      setForm((f) => ({ ...f, [enField]: data.translated }));
      toast.success("تمت الترجمة ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشلت الترجمة");
    } finally {
      setTranslating((s) => ({ ...s, [field]: false }));
    }
  };

  const saveProject = async () => {
    if (!form.title_ar && !form.title_en) {
      toast.error("العنوان مطلوب (عربي أو إنجليزي)");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.id) delete payload.id;
      // Normalize equipment / tools when entered as comma-separated strings
      if (typeof payload.equipment === "string") {
        payload.equipment = payload.equipment.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (typeof payload.tools === "string") {
        payload.tools = payload.tools.split(",").map((s) => s.trim()).filter(Boolean);
      }
      // Sync hero_layout with vision toggle
      if (showVision) {
        payload.hero_layout = "vision";
        payload.hide_meta = true;
        payload.hide_approach = true;
        payload.hide_highlights = true;
        payload.hide_result = true;
        payload.full_width_video = true;
      } else if (payload.hero_layout === "vision") {
        payload.hero_layout = "";
      }
      await axios.post(`${API}/admin/portfolio/projects`, payload, { headers: authHeaders });
      toast.success(form.id ? "تم التحديث" : "تم النشر");
      closeEditor();
      await loadProjects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المشروع نهائياً؟")) return;
    try {
      await axios.delete(`${API}/admin/portfolio/projects/${id}`, { headers: authHeaders });
      toast.success("تم الحذف");
      await loadProjects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحذف");
    }
  };

  const seedProjects = async () => {
    if (!window.confirm("سيتم استيراد المشاريع الأولية من الملف الثابت. المشاريع الموجودة لن تُستبدل. المتابعة؟")) return;
    try {
      const { data } = await axios.post(`${API}/admin/portfolio/seed`, {}, { headers: authHeaders });
      toast.success(`أُدرج ${data.inserted} مشروع، تخطّى ${data.skipped}`);
      await loadProjects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الاستيراد");
    }
  };

  // -------- LOGIN VIEW --------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center p-4" data-testid="admin-portfolio-login">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d10] p-8 md:p-10">
          <div className="w-14 h-14 rounded-full bg-[#E6A95B] text-[#0A0A0C] grid place-items-center mx-auto mb-6">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl text-center mb-2">لوحة تحكم المحفظة</h1>
          <p className="text-white/50 text-center text-sm mb-8">ARTLENSS Portfolio Admin</p>
          <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">كلمة المرور</label>
          <input
            data-testid="admin-portfolio-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLogin()}
            className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-[#E6A95B]"
            autoFocus
          />
          <button
            data-testid="admin-portfolio-login-btn"
            onClick={doLogin}
            disabled={loading}
            className="mt-6 w-full py-3 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            دخول
          </button>
        </div>
      </div>
    );
  }

  // -------- DASHBOARD --------
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="admin-portfolio-dashboard">
      <header className="border-b border-white/10 px-6 py-5">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="text-[#E6A95B] text-xs uppercase tracking-widest">ARTLENSS · Admin</div>
            <div className="font-display text-2xl">إدارة المحفظة</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <a href="/work" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E6A95B] inline-flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" /> معاينة المحفظة
            </a>
            <button
              onClick={seedProjects}
              data-testid="seed-projects-btn"
              className="px-4 py-2 rounded-full border border-white/20 text-white/80 hover:border-[#E6A95B] hover:text-[#E6A95B] text-sm inline-flex items-center gap-2"
              title="استيراد المشاريع الأولية من الملف الثابت (لن يستبدل الموجود)"
            >
              <Database className="w-4 h-4" /> استيراد أولي
            </button>
            <button
              onClick={() => openEditor()}
              data-testid="new-project-btn"
              className="px-4 py-2 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold text-sm inline-flex items-center gap-2 hover:brightness-110"
            >
              <Plus className="w-4 h-4" /> مشروع جديد
            </button>
            <button onClick={doLogout} className="text-white/60 hover:text-white p-2" title="خروج">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <AdminTabs />
      </header>

      <main className="p-6 md:p-10 max-w-6xl mx-auto">
        {projects.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center text-white/60" data-testid="empty-portfolio">
            لا توجد مشاريع بعد — اضغط &quot;مشروع جديد&quot; أو &quot;استيراد أولي&quot; لإضافة المشاريع الحالية.
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-[#0d0d10]" data-testid={`project-row-${p.slug}`}>
                <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-black/40 grid place-items-center">
                  {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <Edit3 className="w-6 h-6 text-white/30" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{p.title_ar || p.title_en || "بدون عنوان"}</div>
                  <div className="text-white/50 text-xs mt-1">
                    /{p.slug} · {p.category} · {p.year} · {p.published ? "منشور" : "مسودة"}
                  </div>
                </div>
                <button onClick={() => openEditor(p)} className="p-2 text-white/70 hover:text-[#E6A95B]" title="تعديل" data-testid={`edit-${p.slug}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => deleteProject(p.id)} className="p-2 text-white/70 hover:text-red-400" title="حذف" data-testid={`delete-${p.slug}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* EDITOR MODAL */}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur overflow-y-auto" data-testid="portfolio-editor-modal">
          <div className="max-w-5xl mx-auto my-10 rounded-2xl border border-white/10 bg-[#0d0d10] p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">{form.id ? "تعديل مشروع" : "مشروع جديد"}</h2>
              <button onClick={closeEditor} className="p-2 text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {/* CORE */}
            <SectionHeader>الأساسيات</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="الرابط (Slug — يُنشأ تلقائياً إن تُرك فارغاً)">
                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="my-project-slug" className="input-admin" data-testid="field-slug" />
              </Field>
              <Field label="السنة">
                <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2025" className="input-admin" />
              </Field>
              <Field label="الفئة">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-admin">
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="ترتيب العرض (الأقل يظهر أولاً)">
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="input-admin" />
              </Field>
              <ImageField
                label="صورة الغلاف"
                value={form.image}
                onChange={(v) => setForm({ ...form, image: v })}
                onUpload={(f) => uploadImage("image", f)}
                uploading={uploading.image}
                testId="image"
              />
              <Field label="رابط الفيديو (YouTube embed أو URL)">
                <input value={form.video} onChange={(e) => setForm({ ...form, video: e.target.value })} placeholder="https://www.youtube.com/embed/VIDEO_ID" className="input-admin" data-testid="field-video" />
              </Field>
              <Field label="رابط خارجي (اختياري — يفتح مباشرة بدل صفحة التفاصيل)">
                <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="" className="input-admin" />
              </Field>
              <ImageField
                label="خلفية قسم النتيجة (اختياري)"
                value={form.result_background}
                onChange={(v) => setForm({ ...form, result_background: v })}
                onUpload={(f) => uploadImage("result_background", f)}
                uploading={uploading.result_background}
                testId="result-bg"
              />
            </div>

            <BiField label="العنوان" field="title" form={form} setForm={setForm} translate={translate} translating={translating.title} />
            <BiField label="الوصف المختصر (Short)" field="short" form={form} setForm={setForm} translate={translate} translating={translating.short} multiline rows={2} />

            {/* META */}
            <SectionHeader>البيانات الوصفية (Meta)</SectionHeader>
            <BiField label="العميل (Client)" field="client" form={form} setForm={setForm} translate={translate} translating={translating.client} />
            <BiField label="الجدول الزمني (Timeline)" field="timeline" form={form} setForm={setForm} translate={translate} translating={translating.timeline} />
            <BiField label="المواقع (Locations)" field="locations" form={form} setForm={setForm} translate={translate} translating={translating.locations} />
            <BiField label="الفريق (Team)" field="team" form={form} setForm={setForm} translate={translate} translating={translating.team} />

            {/* NARRATIVE */}
            <SectionHeader>السرد (Narrative)</SectionHeader>
            <BiField label="الهدف (Goal)" field="goal" form={form} setForm={setForm} translate={translate} translating={translating.goal} multiline rows={3} />
            <BiField label="عنوان قسم المنهجية (Approach headline)" field="approach_headline" form={form} setForm={setForm} translate={translate} translating={translating.approach_headline} />
            <BiField label="المنهجية (Approach)" field="approach" form={form} setForm={setForm} translate={translate} translating={translating.approach} multiline rows={4} />
            <BiField label="نص وصفي (Body)" field="body" form={form} setForm={setForm} translate={translate} translating={translating.body} multiline rows={4} />
            <BiField label="النتيجة (Result)" field="result" form={form} setForm={setForm} translate={translate} translating={translating.result} multiline rows={3} />

            {/* HIGHLIGHTS */}
            <SectionHeader>أبرز التقنيات</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="المعدات (Equipment) — افصل بفاصلة">
                <input
                  value={Array.isArray(form.equipment) ? form.equipment.join(", ") : form.equipment}
                  onChange={(e) => setForm({ ...form, equipment: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Sony FX6, DJI Ronin, Sennheiser"
                  className="input-admin"
                />
              </Field>
              <Field label="الأدوات (Tools) — افصل بفاصلة">
                <input
                  value={Array.isArray(form.tools) ? form.tools.join(", ") : form.tools}
                  onChange={(e) => setForm({ ...form, tools: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="DaVinci Resolve, After Effects"
                  className="input-admin"
                />
              </Field>
            </div>

            {/* LAYOUT FLAGS */}
            <SectionHeader>إعدادات التصميم</SectionHeader>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm text-white/80">
              <Toggle label="إخفاء البيانات الوصفية" checked={form.hide_meta} onChange={(v) => setForm({ ...form, hide_meta: v })} />
              <Toggle label="إخفاء المنهجية" checked={form.hide_approach} onChange={(v) => setForm({ ...form, hide_approach: v })} />
              <Toggle label="إخفاء أبرز التقنيات" checked={form.hide_highlights} onChange={(v) => setForm({ ...form, hide_highlights: v })} />
              <Toggle label="إخفاء النتيجة" checked={form.hide_result} onChange={(v) => setForm({ ...form, hide_result: v })} />
              <Toggle label="فيديو بعرض الشاشة" checked={form.full_width_video} onChange={(v) => setForm({ ...form, full_width_video: v })} />
              <Toggle label="نشر المشروع" checked={form.published} onChange={(v) => setForm({ ...form, published: v })} />
            </div>

            {/* VISION SPECIAL LAYOUT */}
            <SectionHeader>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={showVision} onChange={(e) => setShowVision(e.target.checked)} />
                <span>تخطيط سينمائي خاص (Vision 2030)</span>
              </label>
            </SectionHeader>
            {showVision && (
              <div className="rounded-xl border border-[#E6A95B]/30 bg-black/30 p-4 mb-4">
                <p className="text-xs text-white/60 mb-4">عند التفعيل: يُخفي الميتا والمنهجية والنتيجة تلقائياً، ويعرض عنوان ذهبي كبير + فقرات + فيديو بعرض الشاشة.</p>
                <BiField label="عنوان Vision" field="vision_title" form={form} setForm={setForm} translate={translate} translating={translating.vision_title} />
                <BiField label="فقرات Vision (افصل بين الفقرات بسطر فارغ)" field="vision_paragraphs" form={form} setForm={setForm} translate={translate} translating={translating.vision_paragraphs} multiline rows={8} />
              </div>
            )}

            <div className="mt-8 flex items-center justify-end gap-3">
              <button onClick={closeEditor} className="px-5 py-2.5 rounded-full border border-white/20 text-white/70">إلغاء</button>
              <button
                onClick={saveProject}
                disabled={loading}
                data-testid="save-project-btn"
                className="px-5 py-2.5 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {form.id ? "تحديث" : "نشر"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input-admin { width:100%; background: rgba(0,0,0,0.4); border:1px solid rgba(255,255,255,0.15); border-radius:10px; padding:12px 14px; color:#fff; outline:none; }
        .input-admin:focus { border-color:#E6A95B; }
        .textarea-admin { width:100%; background: rgba(0,0,0,0.4); border:1px solid rgba(255,255,255,0.15); border-radius:10px; padding:12px 14px; color:#fff; outline:none; resize:vertical; font-family: inherit; line-height:1.7; }
        .textarea-admin:focus { border-color:#E6A95B; }
      `}</style>
    </div>
  );
};

const SectionHeader = ({ children }) => (
  <div className="mt-8 mb-4 pb-2 border-b border-white/10">
    <div className="text-[#E6A95B] text-sm uppercase tracking-widest font-bold">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>{label}</span>
  </label>
);

const ImageField = ({ label, value, onChange, onUpload, uploading, testId }) => {
  const inputRef = useRef(null);
  return (
    <div className="mb-4">
      <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">{label}</label>
      <div className="flex items-stretch gap-3">
        <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-black/40 border border-white/10 grid place-items-center">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <span className="text-white/30 text-[10px]">لا صورة</span>
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="ألصق رابطاً أو ارفع صورة"
            className="input-admin"
            data-testid={`field-${testId}-url`}
          />
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
            data-testid={`upload-${testId}-input`}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`upload-${testId}-btn`}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#E6A95B]/50 text-[#E6A95B] text-xs hover:bg-[#E6A95B] hover:text-[#0A0A0C] transition disabled:opacity-50 self-start"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? "جاري الرفع..." : "رفع صورة (حد أقصى 5MB)"}
          </button>
        </div>
      </div>
    </div>
  );
};

const BiField = ({ label, field, form, setForm, translate, translating, multiline, rows = 3 }) => {
  const arField = `${field}_ar`;
  const enField = `${field}_en`;
  return (
    <div className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">{label} · العربية</label>
        {multiline ? (
          <textarea rows={rows} value={form[arField] || ""} onChange={(e) => setForm({ ...form, [arField]: e.target.value })} dir="rtl" className="textarea-admin" data-testid={`field-${arField}`} />
        ) : (
          <input value={form[arField] || ""} onChange={(e) => setForm({ ...form, [arField]: e.target.value })} dir="rtl" className="input-admin" data-testid={`field-${arField}`} />
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs uppercase tracking-widest text-[#E6A95B]">{label} · English</label>
          <button
            type="button"
            onClick={() => translate(field)}
            disabled={translating}
            data-testid={`translate-${field}-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#E6A95B]/50 text-[#E6A95B] text-xs hover:bg-[#E6A95B] hover:text-[#0A0A0C] transition disabled:opacity-50"
          >
            {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
            ترجمة تلقائية
          </button>
        </div>
        {multiline ? (
          <textarea rows={rows} value={form[enField] || ""} onChange={(e) => setForm({ ...form, [enField]: e.target.value })} dir="ltr" className="textarea-admin" data-testid={`field-${enField}`} />
        ) : (
          <input value={form[enField] || ""} onChange={(e) => setForm({ ...form, [enField]: e.target.value })} dir="ltr" className="input-admin" data-testid={`field-${enField}`} />
        )}
      </div>
    </div>
  );
};

export default AdminPortfolio;
